import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Search, 
  Filter, 
  Eye, 
  EyeOff,
  User,
  Lock,
  Save,
  RefreshCw
} from 'lucide-react';
import { userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const PasswordManagement = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { data: users, isLoading } = useQuery(
    'all-users-for-password',
    () => {
      if (user?.role === 'SUPER_ADMIN') {
        return userAPI.getAllUsers({ includeInactive: true });
      } else if (user?.role === 'ADMIN') {
        return userAPI.getManagersAndEmployeesAdmin();
      }
      return Promise.reject(new Error('Unauthorized'));
    },
    {
      enabled: user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN',
      select: (response) => {
        console.log('PasswordManagement - API Response:', response);
        console.log('PasswordManagement - API Response type:', typeof response);
        console.log('PasswordManagement - API Response keys:', Object.keys(response || {}));
        console.log('PasswordManagement - User role:', user?.role);
        
        if (user?.role === 'ADMIN') {
          // Admin response structure: response.data = { data: [...], total: ... }
          console.log('PasswordManagement - response.data:', response?.data);
          console.log('PasswordManagement - response.data.data:', response?.data?.data);
          console.log('PasswordManagement - response.data.data type:', typeof response?.data?.data);
          console.log('PasswordManagement - response.data.data isArray:', Array.isArray(response?.data?.data));
          
          const adminUsers = response?.data?.data;
          console.log('PasswordManagement - Admin users:', adminUsers);
          console.log('PasswordManagement - Admin users type:', typeof adminUsers);
          console.log('PasswordManagement - Admin users isArray:', Array.isArray(adminUsers));
          
          if (Array.isArray(adminUsers)) {
            console.log('PasswordManagement - Returning admin array with', adminUsers.length, 'users');
            return adminUsers;
          } else {
            console.log('PasswordManagement - Admin users not an array, returning empty array');
            return [];
          }
        } else {
          // Super Admin response structure: response.data = { users: [...], total: ... }
          console.log('PasswordManagement - response.data:', response?.data);
          console.log('PasswordManagement - response.data.users:', response?.data?.users);
          console.log('PasswordManagement - response.data.users type:', typeof response?.data?.users);
          console.log('PasswordManagement - response.data.users isArray:', Array.isArray(response?.data?.users));
          
          const superAdminUsers = response?.data?.users;
          console.log('PasswordManagement - Super Admin users:', superAdminUsers);
          console.log('PasswordManagement - Super Admin users type:', typeof superAdminUsers);
          console.log('PasswordManagement - Super Admin users isArray:', Array.isArray(superAdminUsers));
          
          if (Array.isArray(superAdminUsers)) {
            console.log('PasswordManagement - Returning array with', superAdminUsers.length, 'users');
            return superAdminUsers;
          } else {
            console.log('PasswordManagement - Not an array, returning empty array');
            return [];
          }
        }
      },
    }
  );

  const updatePasswordMutation = useMutation(
    ({ userId, password }) => {
      if (user?.role === 'ADMIN') {
        return userAPI.updateUserPasswordAdmin(userId, { password });
      } else {
        return userAPI.updateUserPassword(userId, { password });
      }
    },
    {
      onSuccess: () => {
        toast.success('Password updated successfully');
        queryClient.invalidateQueries('all-users-for-password');
        setShowPasswordModal(false);
        setNewPassword('');
        setConfirmPassword('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update password');
      },
    }
  );

  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    const matchesSearch = 
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  }) : [];

  const handleUpdatePassword = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const handleSubmitPassword = () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    updatePasswordMutation.mutate({
      userId: selectedUser.id,
      password: newPassword
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return <Lock className="w-4 h-4 text-yellow-500" />;
      case 'ADMIN':
        return <Lock className="w-4 h-4 text-blue-500" />;
      case 'EMPLOYEE':
        return <User className="w-4 h-4 text-green-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-4 md:p-6"
      >
        <div className="flex items-center space-x-3">
          <Lock className="w-7 h-7 md:w-8 md:h-8 text-primary-600 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Password Management</h1>
            <p className="text-sm md:text-base text-gray-600 mt-0.5 md:mt-1">
              Update passwords for users who have forgotten them
            </p>
          </div>
        </div>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-4 md:p-6"
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </motion.div>

      {/* Users List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm"
      >
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
          <h2 className="text-base md:text-lg font-medium text-gray-900">
            Users ({filteredUsers.length})
          </h2>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredUsers.map((user, index) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="px-4 md:px-6 py-3 md:py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3 md:gap-4 min-w-0 flex-1">
                  {/* Avatar - hidden on mobile */}
                  <span className="hidden md:block flex-shrink-0">
                    <div className="h-12 w-12 rounded-full bg-primary-500 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                      </span>
                    </div>
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium text-gray-900">
                      {user.firstName} {user.lastName}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-500 max-md:mt-0.5">
                      <span className="hidden md:inline">{user.department || 'No Department'}</span>
                      <span className="hidden md:inline">{user.position || 'No Position'}</span>
                      <span className="md:flex md:items-center md:gap-1">
                        {getRoleIcon(user.role?.name || user.role)}
                        <span>{user.role?.name || user.role}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={() => handleUpdatePassword(user)}
                    className="w-full md:w-auto px-4 py-3 md:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 touch-manipulation text-sm font-medium"
                  >
                    <Lock className="w-4 h-4" />
                    <span>Update Password</span>
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {filteredUsers.length === 0 && (
          <div className="px-4 md:px-6 py-6 md:py-8 text-center">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm 
                ? 'Try adjusting your search criteria.'
                : 'No users have been added yet.'
              }
            </p>
          </div>
        )}
      </motion.div>

      {/* Password Update Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Update Password</h3>
              <p className="text-sm text-gray-500 mt-1">
                Set a new password for {selectedUser.firstName} {selectedUser.lastName}
              </p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-10"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-10"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitPassword}
                  disabled={updatePasswordMutation.isLoading || !newPassword || !confirmPassword}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {updatePasswordMutation.isLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>Update Password</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PasswordManagement;
